import React from 'react';
import { Chip, ChipProps } from '@mui/material';
import { getDomainColor, getDomainColorLight } from '../utils/domainColors';
import { formatDocumentCode } from '../utils/documentCodeUtils';

export interface DocumentChipProps extends Omit<ChipProps, 'label' | 'icon'> {
  document: {
    id: string;
    title: string;
    code?: string;
    domain?: string;
    docDomain?: string;
  };
  onClick?: () => void;
  size?: 'small' | 'medium';
  variant?: 'outlined' | 'filled';
}

const DocumentChip: React.FC<DocumentChipProps> = ({
  document,
  onClick,
  size = 'small',
  variant = 'outlined',
  sx = {},
  ...chipProps
}) => {
  const docDomain = document.domain || document.docDomain || 'EX';
  const docColor = getDomainColor(docDomain);
  const docColorLight = getDomainColorLight(docDomain);
  const formattedCode = document.code ? formatDocumentCode(document.code) : null;
  const displayLabel = formattedCode ? `${formattedCode} ${document.title}` : document.title;
  const isInteractive = !!onClick;

  return (
    <Chip
      label={displayLabel}
      variant={variant}
      size={size}
      onClick={isInteractive ? onClick : undefined}
      sx={{
        cursor: isInteractive ? 'pointer' : 'default',
        color: docColor,
        fontSize: size === 'small' ? '0.75em' : '0.9em',
        borderWidth: variant === 'outlined' ? 2 : undefined,
        borderColor: variant === 'outlined' ? docColorLight : undefined,
        backgroundColor: variant === 'outlined' ? `${docColorLight}20` : undefined,
        '&:hover': isInteractive
          ? {
              borderColor: variant === 'outlined' ? docColor : undefined,
              backgroundColor: variant === 'outlined' ? `${docColorLight}40` : undefined,
            }
          : {},
        transition: 'all 0.2s ease',
        ...sx,
      }}
      role={isInteractive ? 'button' : undefined}
      aria-label={isInteractive ? `Open document: ${displayLabel}` : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      {...chipProps}
    />
  );
};

export default DocumentChip;
